/**
 * KitchenScene - Kitchen scene for Pip's Journey
 * Simplified version matching the style of other scenes
 */

import { useState, useEffect } from 'react';

export default function KitchenScene({ level, isComplete, isListening }) {
  const [fridgeOpen, setFridgeOpen] = useState(false);
  const [pipMood, setPipMood] = useState('happy');
  const [pipHolding, setPipHolding] = useState(null);
  const [sparkles, setSparkles] = useState(false);

  useEffect(() => {
    const setup = level?.kitchenSetup || {};
    setFridgeOpen(setup.fridgeOpen || false);
    setPipHolding(setup.pipHolding || null);
    setPipMood(setup.pipMood || 'happy');
    setSparkles(false);
  }, [level?.id]);

  useEffect(() => {
    if (isComplete) {
      setSparkles(true);
      // Animate based on action
      const action = level?.kitchenAction;
      if (action === 'openFridge') setFridgeOpen(true);
      if (action === 'getJuice') setPipHolding('🧃');
      if (action === 'getApple') setPipHolding('🍎');
      if (action === 'eatApple') setPipMood('eating');
    }
  }, [isComplete, level]);

  return (
    <div style={styles.scene}>
      {/* Kitchen background */}
      <div style={styles.kitchen}>
        <div style={styles.wall} />
        <div style={styles.floor} />
        
        {/* Counter */}
        <div style={styles.counter} />
        
        {/* Window */}
        <div style={styles.window}>
          <div style={styles.windowSky} />
        </div>
        
        {/* Fridge */}
        <div style={styles.fridgeContainer}>
          <div style={{
            ...styles.fridge,
            transform: fridgeOpen ? 'perspective(500px) rotateY(-30deg)' : 'none',
          }}>
            <div style={styles.fridgeHandle} />
          </div>
          {fridgeOpen && (
            <div style={styles.fridgeInside}>
              <span style={styles.fridgeItem}>🧃</span>
              <span style={styles.fridgeItem}>🍎</span>
              <span style={styles.fridgeItem}>🥛</span>
            </div>
          )}
        </div>
      </div>

      {/* Pip character */}
      <div style={styles.pipContainer}>
        <div style={styles.pip}>
          {/* Body */}
          <div style={styles.pipBody}>
            {/* Ears */}
            <div style={styles.pipEars}>
              <div style={styles.pipEar} />
              <div style={styles.pipEar} />
            </div>
            {/* Face */}
            <div style={styles.pipFace}>
              <div style={styles.pipEyes}>
                <div style={styles.pipEye} />
                <div style={styles.pipEye} />
              </div>
              <div style={{
                ...styles.pipMouth,
                borderRadius: pipMood === 'eating' ? '50%' : '0 0 20px 20px',
                height: pipMood === 'eating' ? '15px' : '12px',
                width: pipMood === 'eating' ? '15px' : '25px',
              }} />
            </div>
          </div>
          {/* Held item */}
          {pipHolding && (
            <div style={styles.heldItem}>{pipHolding}</div>
          )}
        </div>
        
        {/* Celebration sparkles */}
        {sparkles && (
          <div style={styles.sparkles}>
            {['✨', '⭐', '🌟', '💫'].map((s, i) => (
              <span key={i} style={{
                ...styles.sparkle,
                animationDelay: `${i * 0.1}s`,
                left: `${20 + i * 20}%`,
              }}>{s}</span>
            ))}
          </div>
        )}
      </div>

      {/* Listening indicator */}
      {isListening && (
        <div style={styles.listening}>🎤 Listening...</div>
      )}
    </div>
  );
}

const styles = {
  scene: { position: 'absolute', inset: 0, overflow: 'hidden' },
  kitchen: { position: 'absolute', inset: 0 },
  wall: { position: 'absolute', top: 0, left: 0, right: 0, height: '70%', background: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)' },
  floor: { position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', background: 'linear-gradient(180deg, #a16207 0%, #854d0e 100%)' },
  counter: { position: 'absolute', bottom: '25%', left: '10%', right: '10%', height: '15%', background: 'linear-gradient(180deg, #78716c 0%, #57534e 100%)', borderRadius: '5px 5px 0 0' },
  window: { position: 'absolute', top: '15%', left: '15%', width: '80px', height: '80px', background: 'white', borderRadius: '5px', border: '8px solid #78716c', overflow: 'hidden' },
  windowSky: { position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #38bdf8 0%, #7dd3fc 100%)' },
  fridgeContainer: { position: 'absolute', top: '15%', right: '10%', width: '70px', height: '140px' },
  fridge: { position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #e2e8f0 0%, #cbd5e1 100%)', borderRadius: '5px', transition: 'transform 0.3s ease', transformOrigin: 'right' },
  fridgeHandle: { position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', width: '6px', height: '30px', background: '#94a3b8', borderRadius: '3px' },
  fridgeInside: { position: 'absolute', inset: '5px', background: '#f1f5f9', borderRadius: '3px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around', padding: '10px 0' },
  fridgeItem: { fontSize: '20px' },
  pipContainer: { position: 'absolute', bottom: '28%', left: '50%', transform: 'translateX(-50%)' },
  pip: { position: 'relative', animation: 'pipIdle 2s ease-in-out infinite' },
  pipBody: { width: '80px', height: '80px', background: 'linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)', borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%', position: 'relative', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' },
  pipEars: { position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', width: '65px', display: 'flex', justifyContent: 'space-between' },
  pipEar: { width: '20px', height: '28px', background: 'linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)', borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%' },
  pipFace: { position: 'absolute', top: '30%', left: '50%', transform: 'translateX(-50%)' },
  pipEyes: { display: 'flex', gap: '15px', marginBottom: '8px' },
  pipEye: { width: '8px', height: '8px', background: '#1e293b', borderRadius: '50%' },
  pipMouth: { width: '25px', height: '12px', border: '3px solid #1e293b', borderTop: 'none', borderRadius: '0 0 20px 20px', margin: '0 auto' },
  heldItem: { position: 'absolute', bottom: '-10px', right: '-20px', fontSize: '35px' },
  sparkles: { position: 'absolute', top: '-40px', left: '50%', transform: 'translateX(-50%)', width: '100px' },
  sparkle: { position: 'absolute', fontSize: '20px', animation: 'sparkleFloat 0.8s ease-out forwards' },
  listening: { position: 'absolute', bottom: '10%', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', color: 'white', padding: '10px 20px', borderRadius: '20px', fontSize: '14px' },
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pipIdle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
  @keyframes sparkleFloat { 0% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-30px); } }
`;
if (!document.querySelector('#kitchen-scene-styles')) {
  styleSheet.id = 'kitchen-scene-styles';
  document.head.appendChild(styleSheet);
}
